import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { MessageCircle } from "lucide-react";

export const Stack24Section = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [phase, setPhase] = useState<"falling" | "collapsed">("falling");

  useEffect(() => {
    if (!isInView) return;
    const t = setTimeout(() => setPhase("collapsed"), 2800);
    return () => clearTimeout(t);
  }, [isInView]);

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 noise-texture" />
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      <div className="container relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Left */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-lg mb-4">The 24-Hour Stack.</h2>
            <p className="text-lg text-primary font-medium mb-4">From Chaos to Clarity.</p>
            <p className="body-lg text-muted-foreground">
              Never get bombarded by 10 notifications from the same client again. ClearMakes intelligently groups consecutive messages from the last 24 hours into a single, digestible card. See the full context before you reply.
            </p>
          </motion.div>

          {/* Animated UI Right */}
          <motion.div
            className="relative min-h-[320px] flex items-center justify-center"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Falling bubbles */}
            {phase === "falling" && (
              <>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center"
                    initial={{ top: "-20px", left: `${20 + i * 10}%`, opacity: 1 }}
                    animate={{
                      top: "50%",
                      left: "50%",
                      x: "-50%",
                      y: "-50%",
                      opacity: 0,
                      scale: 0.3,
                    }}
                    transition={{
                      duration: 1.2,
                      delay: i * 0.15,
                      ease: "easeIn",
                    }}
                  >
                    <MessageCircle className="w-4 h-4 text-white/60" />
                  </motion.div>
                ))}
              </>
            )}

            {/* Collapsed glass card */}
            <motion.div
              className="w-full max-w-sm rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-5 shadow-xl"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={phase === "collapsed" && isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div className="inline-block px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-medium mb-4">
                8 Messages from John Doe
              </div>
              <p className="text-sm text-muted-foreground">
                All messages from the last 24 hours grouped into one card. Click to expand and reply with full context.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
